import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import StudentLayout from '@/components/layouts/StudentLayout';
import { 
  Target, ArrowLeft, Trophy, Clock, Star, 
  RotateCcw, Zap
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const NumberNinjaGame = () => {
  const navigate = useNavigate();
  const { api } = useAuth();
  const inputRef = useRef(null);
  
  const [gameState, setGameState] = useState('ready');
  const [timeLeft, setTimeLeft] = useState(90);
  const [score, setScore] = useState(0);
  const [level, setLevel] = useState(1);
  const [currentProblem, setCurrentProblem] = useState(null);
  const [userAnswer, setUserAnswer] = useState('');
  const [streak, setStreak] = useState(0);
  const [problemsCompleted, setProblemsCompleted] = useState(0);
  const [showFeedback, setShowFeedback] = useState(null);

  const generateProblem = useCallback(() => {
    const operations = ['+', '-', '×'];
    const op = operations[Math.floor(Math.random() * operations.length)];
    
    let num1, num2, answer;
    const maxNum = 10 + level * 5; // Increases with level
    
    switch (op) {
      case '+':
        num1 = Math.floor(Math.random() * maxNum) + 1;
        num2 = Math.floor(Math.random() * maxNum) + 1;
        answer = num1 + num2;
        break;
      case '-':
        num1 = Math.floor(Math.random() * maxNum) + 10;
        num2 = Math.floor(Math.random() * Math.min(num1, maxNum)) + 1;
        answer = num1 - num2;
        break;
      case '×':
        num1 = Math.floor(Math.random() * 12) + 1;
        num2 = Math.floor(Math.random() * 12) + 1;
        answer = num1 * num2;
        break;
      default:
        num1 = 1;
        num2 = 1;
        answer = 2;
    }
    
    return {
      display: `${num1} ${op} ${num2}`,
      answer: answer.toString(),
      points: op === '×' ? 15 : 10
    };
  }, [level]);

  const startGame = useCallback(() => {
    setGameState('playing');
    setTimeLeft(90);
    setScore(0);
    setLevel(1);
    setStreak(0);
    setProblemsCompleted(0);
    setCurrentProblem(generateProblem());
    setUserAnswer('');
    setShowFeedback(null);
    setTimeout(() => inputRef.current?.focus(), 100);
  }, [generateProblem]);

  // Timer
  useEffect(() => {
    if (gameState === 'playing' && timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(t => t - 1), 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0 && gameState === 'playing') {
      endGame();
    }
  }, [gameState, timeLeft]);

  // Level up every 5 correct answers
  useEffect(() => {
    if (problemsCompleted > 0 && problemsCompleted % 5 === 0) {
      setLevel(l => l + 1);
      toast.success(`Level ${level + 1}! Problems getting harder!`);
    }
  }, [problemsCompleted]);

  const endGame = async () => {
    setGameState('finished');
    
    try {
      await api.post('/games/complete', {
        game_id: 'number-ninja',
        score: score,
        time_seconds: 90 - timeLeft,
        metadata: { level, problems_completed: problemsCompleted, best_streak: streak }
      });
      toast.success(`🎉 You earned ${score} points!`);
    } catch (error) {
      console.error('Error saving game:', error);
    }
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (gameState !== 'playing' || !userAnswer) return;
    
    const isCorrect = userAnswer === currentProblem.answer;
    
    if (isCorrect) {
      const points = currentProblem.points + (streak * 2);
      setScore(s => s + points);
      setStreak(s => s + 1);
      setProblemsCompleted(p => p + 1);
      setShowFeedback('correct');
    } else {
      setStreak(0);
      setShowFeedback('wrong');
    }
    
    setTimeout(() => {
      setCurrentProblem(generateProblem());
      setUserAnswer('');
      setShowFeedback(null);
      inputRef.current?.focus();
    }, 500);
  };

  return (
    <StudentLayout>
      <div data-testid="number-ninja-game" className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button 
            onClick={() => navigate('/student/games')}
            className="flex items-center gap-2 text-slate-600 hover:text-purple-600 font-medium"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Games
          </button>
          
          {gameState === 'playing' && (
            <div className="flex items-center gap-4">
              <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${timeLeft <= 15 ? 'bg-red-100' : 'bg-emerald-100'}`}>
                <Clock className={`w-5 h-5 ${timeLeft <= 15 ? 'text-red-600' : 'text-emerald-600'}`} />
                <span className={`font-heading text-xl font-bold ${timeLeft <= 15 ? 'text-red-600' : 'text-emerald-700'}`}>
                  {timeLeft}s
                </span>
              </div>
              <div className="flex items-center gap-2 bg-amber-100 px-4 py-2 rounded-full">
                <Star className="w-5 h-5 text-amber-600" />
                <span className="font-heading text-xl font-bold text-amber-700">{score}</span>
              </div>
            </div>
          )}
        </div>

        {/* Game Header */}
        <div className="student-card bg-emerald-100 border-emerald-300 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-emerald-500 rounded-2xl border-2 border-gray-800 flex items-center justify-center">
                <Target className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="font-heading text-2xl font-bold text-slate-800">Number Ninja</h1>
                <p className="text-slate-600">
                  {gameState === 'playing' ? `Level ${level} • Streak: ${streak}` : 'Fast maths challenge!'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Ready Screen */}
        {gameState === 'ready' && (
          <div className="student-card text-center py-12">
            <div className="w-20 h-20 bg-emerald-400 rounded-full flex items-center justify-center mx-auto mb-6">
              <Target className="w-12 h-12 text-white" />
            </div>
            <h2 className="font-heading text-3xl font-bold text-slate-800 mb-4">Number Ninja</h2>
            <p className="text-lg text-slate-600 mb-8">
              Solve maths problems as fast as you can!<br/>
              90 seconds - Build streaks for bonus points!
            </p>
            <Button onClick={startGame} className="student-btn text-lg px-10 py-6">
              Start Challenge
              <Zap className="w-6 h-6 ml-2" />
            </Button>
          </div>
        )}

        {/* Playing Screen */}
        {gameState === 'playing' && currentProblem && (
          <div className="student-card text-center">
            {streak >= 3 && (
              <div className="mb-4">
                <span className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 text-amber-700 rounded-full font-bold animate-pulse">
                  🔥 {streak} streak! +{streak * 2} bonus
                </span>
              </div>
            )}
            
            <div className={`text-6xl font-heading font-bold mb-8 transition-all ${
              showFeedback === 'correct' ? 'text-emerald-500 scale-110' :
              showFeedback === 'wrong' ? 'text-red-500 shake' : 'text-slate-800'
            }`}>
              {currentProblem.display} = ?
            </div>
            
            <form onSubmit={handleSubmit} className="flex gap-4 justify-center items-center">
              <Input
                ref={inputRef}
                type="number"
                value={userAnswer}
                onChange={(e) => setUserAnswer(e.target.value)}
                className="w-40 text-center text-3xl font-bold h-16"
                placeholder="?"
                autoFocus
                data-testid="answer-input"
              />
              <Button type="submit" className="student-btn h-16 px-8">
                <Zap className="w-6 h-6" />
              </Button>
            </form>
            
            <p className="mt-6 text-slate-500">
              Problems solved: {problemsCompleted}
            </p>
          </div>
        )}

        {/* Finished Screen */}
        {gameState === 'finished' && (
          <div className="student-card text-center py-12">
            <div className="w-20 h-20 bg-amber-400 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
              <Trophy className="w-12 h-12 text-white" />
            </div>
            <h2 className="font-heading text-3xl font-bold text-slate-800 mb-2">Time's Up!</h2>
            <p className="text-xl text-slate-600 mb-4">
              You reached Level {level}!
            </p>
            
            <div className="grid grid-cols-2 gap-4 my-8 max-w-xs mx-auto">
              <div className="bg-slate-50 rounded-xl p-4">
                <p className="text-3xl font-bold text-emerald-600">{problemsCompleted}</p>
                <p className="text-sm text-slate-500">Solved</p>
              </div>
              <div className="bg-slate-50 rounded-xl p-4">
                <p className="text-3xl font-bold text-purple-600">{level}</p>
                <p className="text-sm text-slate-500">Level</p>
              </div>
            </div>
            
            <div className="flex items-center justify-center gap-2 mb-8">
              <Star className="w-8 h-8 text-amber-400 fill-amber-400" />
              <span className="font-heading text-4xl font-bold text-amber-600">+{score}</span>
              <span className="text-slate-600 text-xl">points</span>
            </div>
            
            <div className="flex gap-4 justify-center">
              <Button variant="outline" onClick={startGame}>
                <RotateCcw className="w-4 h-4 mr-2" />
                Play Again
              </Button>
              <Button onClick={() => navigate('/student/games')} className="student-btn">
                More Games
              </Button>
            </div>
          </div>
        )}
      </div>
    </StudentLayout>
  );
};

export default NumberNinjaGame;
